import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Popover,
  Tooltip,
} from '@mui/material';
import { AttributesTable } from 'interfaces/inferenceArtifacts';
import { PostProcessingActionCreated } from 'interfaces/postProcessingAction';
import { StainType } from 'interfaces/stainType';
import { Taxonomy } from 'interfaces/taxonomy';
import { Dictionary, filter, findIndex, first, isArray, isEmpty, isEqual, map, pullAt, size, some } from 'lodash';
import React, { useState } from 'react';
import PreviousJobComparisonAlert from '../PreviousJobComparisonAlert';
import type { PreviousJobComparisonStatus } from '../previousJobComparisonStatus';
import { HighlightedPresetInput, PresetActionAccess } from '../Presets/presetUtils';
import { PostProcessingAction } from './PostProcessingAction';
import { useActionsQuery } from './usePostProcessingActions';
import { createNewAction, getActionsWithCorrectNewOptionValueAfterIndexChange } from './utils';

interface PostProcessingActionsProps {
  actions: PostProcessingActionCreated[];
  setActions: React.Dispatch<React.SetStateAction<PostProcessingActionCreated[]>>;
  updateClassConfigAccordingToActions: (actions: PostProcessingActionCreated[]) => void;
  getOptions: (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) => any[];
  getOptionsByValue: (
    optionSource: string | string[],
    selectedStains: string[],
    index: number,
    actionInput: string,
    inputSourceDependentOn?: string
  ) => Dictionary<{ id: string | number; label: string }>;
  getMappingFiltersMetadataForLogicalQuery: (
    stain: string,
    actionInput: string,
    actionAttributesTable: AttributesTable,
    index: number
  ) => any;
  previousJobComparisonStatus?: PreviousJobComparisonStatus;
  highlightedInputs?: HighlightedPresetInput[];
  stainTypes?: StainType[];
  taxonomies?: Taxonomy[];
  qcMarkersSettings?: Record<string, string>;
  presetAccessByActionId?: Record<string, PresetActionAccess>;
  onPresetAccessChange?: (actionId: string, access: PresetActionAccess) => void;
}

export const PostProcessingActions: React.FC<React.PropsWithChildren<PostProcessingActionsProps>> = ({
  actions,
  setActions,
  updateClassConfigAccordingToActions,
  getOptions,
  getOptionsByValue,
  getMappingFiltersMetadataForLogicalQuery,
  previousJobComparisonStatus,
  highlightedInputs = [],
  stainTypes = [],
  taxonomies = [],
  qcMarkersSettings = {},
  presetAccessByActionId,
  onPresetAccessChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [actionInsertionIndex, setActionInsertionIndex] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { postProcessingActionsById, postProcessingActionsAreas, postProcessingActionsCells } = useActionsQuery();

  const closeActionPicker = () => {
    setAnchorEl(null);
    setActionInsertionIndex(null);
  };

  const openActionPicker = (event: React.MouseEvent<HTMLButtonElement>, insertionIndex: number) => {
    event.stopPropagation();
    setActionInsertionIndex(insertionIndex);
    setAnchorEl(event.currentTarget);
  };

  const addNewAction = (postProcessingActionId: string) => {
    if (actionInsertionIndex === null) {
      return;
    }

    closeActionPicker();
    const newAction = createNewAction(postProcessingActionId, postProcessingActionsById);

    setActions((prevActions) => {
      const newActions = [...prevActions];
      newActions.splice(actionInsertionIndex, 0, newAction);
      updateClassConfigAccordingToActions(newActions);
      return newActions;
    });
  };

  const moveAction = (oldIndex: number, newIndex: number) => {
    if (
      oldIndex === newIndex ||
      oldIndex < 0 ||
      newIndex < 0 ||
      oldIndex >= size(actions) ||
      newIndex >= size(actions)
    ) {
      return;
    }

    setActions((prevActions) => {
      const correctnessActions = getActionsWithCorrectNewOptionValueAfterIndexChange(
        prevActions,
        oldIndex,
        newIndex,
        postProcessingActionsById,
        getOptionsByValue
      );
      const newActions = arrayMove(correctnessActions, oldIndex, newIndex);
      updateClassConfigAccordingToActions(newActions);
      return newActions;
    });
  };

  const removeAction = (index: number) => {
    setActions((prevActions) => {
      const correctNewActions = getActionsWithCorrectNewOptionValueAfterIndexChange(
        prevActions,
        index,
        size(prevActions) - 1,
        postProcessingActionsById,
        getOptionsByValue
      );
      pullAt(correctNewActions, index);
      updateClassConfigAccordingToActions(correctNewActions);
      return correctNewActions;
    });
  };

  // Check if any change is NOT just from null to string
  const isSignificantArrayChange = (prev: (string | null)[], next: (string | null)[]): boolean => {
    if (isEqual(prev, next)) return false;

    return some(prev, (value, idx) => {
      const nextValue = next[idx];
      // If changed and prev value is not null
      return value !== nextValue && value !== null;
    });
  };

  // Get differences between two arrays between the same indices, returning pairs of values that differ.
  // For example, if arr1 = ['a', 'b', null] and arr2 = ['a', 'c', null], it returns [['b', 'c']].
  function getArrayDifferences(arr1: (string | null)[], arr2: (string | null)[]): (string | null)[][] {
    return filter(
      map(arr1, (value, idx) => (value !== arr2[idx] ? [value, arr2[idx]] : null)),
      Boolean
    ) as (string | null)[][];
  }

  const setAction = (action: any, index: number) => {
    setActions((prevActions) => {
      let newActions = [...prevActions];
      newActions[index] = action;

      // if a value of a newOption has changed and the previous newOptionValues is not null
      // look for the oldValue in actions and change to the new value
      const diffs = getArrayDifferences(prevActions[index].newOptionValues, action.newOptionValues);

      if (
        prevActions[index].newOptionValues &&
        isSignificantArrayChange(prevActions[index].newOptionValues, action.newOptionValues) &&
        size(diffs) === 1
      ) {
        const diff = first(diffs);
        newActions = getUpdateActions(newActions, action.actionInput, action.stain, diff[0], diff[1]);
      }

      return newActions;
    });
  };

  const handleActionRecursively = (
    action: any,
    actionInput: string,
    oldValue: string,
    newValue: string | null
  ): any => {
    if (isArray(action)) {
      if (isEmpty(newValue)) {
        // if newValue is null, we don't want to change the action
        return action;
      } else {
        // replace oldValue with newValue in the array
        return map(action, (item) => (item === oldValue ? newValue : item));
      }
    } else if (typeof action === 'object' && action !== null) {
      const updatedAction: Record<string, any> = {};
      for (const key in action) {
        updatedAction[key] = handleActionRecursively(action[key], actionInput, oldValue, newValue);
      }
      return updatedAction;
    } else if (action === oldValue) {
      return newValue;
    } else {
      return action;
    }
  };

  const getUpdateActions = (
    newActions: any,
    actionInput: string,
    actionStain: string,
    oldValue: string,
    newValue: string
  ): any[] => {
    return map(newActions, (action) => {
      if (action.actionInput === actionInput && action.stain === actionStain)
        return handleActionRecursively(action, actionInput, oldValue, newValue);
      else return action;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const activeIndex = findIndex(actions, (item) => item.id === active?.id);
      const overIndex = findIndex(actions, (item) => item.id === over?.id);
      moveAction(activeIndex, overIndex);
    }
  };

  const getActionInsertionButtonLabel = (insertionIndex: number) => {
    if (isEmpty(actions)) {
      return 'Add action';
    }
    if (insertionIndex === 0) {
      return 'Add action before step 1';
    }
    if (insertionIndex === size(actions)) {
      return `Add action after step ${size(actions)}`;
    }

    return `Add action between steps ${insertionIndex} and ${insertionIndex + 1}`;
  };

  // Insertion points at the very start or end of the list are the primary "Add Action" affordance, so they get a
  // prominent, left-aligned button. Insertion points between two existing actions are secondary, so they get a
  // subtle, centered inline "+" control instead of competing for attention with the primary button.
  const isEdgeInsertionIndex = (insertionIndex: number) =>
    isEmpty(actions) || insertionIndex === 0 || insertionIndex === size(actions);

  const renderPrimaryActionInsertionButton = (insertionIndex: number) => (
    <Grid item key={`add-action-${insertionIndex}`} sx={{ py: 1 }}>
      <Button
        aria-describedby="post-processing-action-picker"
        variant="outlined"
        size="small"
        startIcon={<AddIcon fontSize="small" />}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => openActionPicker(event, insertionIndex)}
        sx={{ borderRadius: 5 }}
      >
        {getActionInsertionButtonLabel(insertionIndex)}
      </Button>
    </Grid>
  );

  const renderIntermediateActionInsertionButton = (insertionIndex: number) => (
    <Grid item key={`add-action-${insertionIndex}`}>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        sx={{ position: 'relative', py: 0.5, '&:hover .insert-action-divider': { borderColor: 'primary.main' } }}
      >
        <Divider
          className="insert-action-divider"
          sx={{ position: 'absolute', left: 0, right: 0, borderColor: 'divider' }}
        />
        <Tooltip title={getActionInsertionButtonLabel(insertionIndex)}>
          <IconButton
            aria-label={getActionInsertionButtonLabel(insertionIndex)}
            aria-describedby="post-processing-action-picker"
            size="small"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => openActionPicker(event, insertionIndex)}
            sx={{
              zIndex: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              color: 'primary.main',
              '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText', borderColor: 'primary.main' },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );

  const renderActionInsertionButton = (insertionIndex: number) =>
    isEdgeInsertionIndex(insertionIndex)
      ? renderPrimaryActionInsertionButton(insertionIndex)
      : renderIntermediateActionInsertionButton(insertionIndex);

  return (
    <Grid container>
      {previousJobComparisonStatus?.hasPreviousJobSettings && (
        <Grid item xs={12} mb={2}>
          <PreviousJobComparisonAlert
            status={previousJobComparisonStatus}
            modifiedMessage="Post-processing actions modified from previous job."
            matchMessage="Post-processing actions match previous job."
          />
        </Grid>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={actions} strategy={verticalListSortingStrategy}>
          <Grid container direction="column">
            {renderActionInsertionButton(0)}
            {map(actions, (action, index) => {
              return (
                <Grid item mb={1} key={action.id}>
                  <PostProcessingAction
                    key={action.id}
                    id={action.id}
                    index={index}
                    isFirst={index === 0}
                    isLast={index === size(actions) - 1}
                    postProcessingActionsById={postProcessingActionsById}
                    action={action}
                    setAction={setAction}
                    removeAction={removeAction}
                    moveActionUp={() => moveAction(index, index - 1)}
                    moveActionDown={() => moveAction(index, index + 1)}
                    getOptions={getOptions}
                    getOptionsByValue={getOptionsByValue}
                    getMappingFiltersMetadataForLogicalQuery={getMappingFiltersMetadataForLogicalQuery}
                    presetInputSection="postprocessing"
                    highlightedInputs={highlightedInputs}
                    stainTypes={stainTypes}
                    taxonomies={taxonomies}
                    qcMarkersSettings={qcMarkersSettings}
                    presetAuthoring={
                      onPresetAccessChange
                        ? {
                            access: presetAccessByActionId?.[action.id] || 'editable',
                            onAccessChange: (access) => onPresetAccessChange(action.id, access),
                          }
                        : undefined
                    }
                  />
                  {renderActionInsertionButton(index + 1)}
                </Grid>
              );
            })}
          </Grid>
        </SortableContext>
      </DndContext>
      <Popover
        id="post-processing-action-picker"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeActionPicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          <ListSubheader>{`Areas`}</ListSubheader>
          {map(
            filter(postProcessingActionsAreas, (postProcessingAction) => isEmpty(postProcessingAction.deletedAt)),
            (postProcessingAction) => (
              <ListItemButton key={postProcessingAction.id} onClick={() => addNewAction(postProcessingAction.id)}>
                <ListItemText primary={postProcessingAction.actionDisplayName ?? postProcessingAction.actionName} />
              </ListItemButton>
            )
          )}
          <ListSubheader>{`Cells`}</ListSubheader>
          {map(
            filter(postProcessingActionsCells, (postProcessingAction) => isEmpty(postProcessingAction.deletedAt)),
            (postProcessingAction) => (
              <ListItemButton key={postProcessingAction.id} onClick={() => addNewAction(postProcessingAction.id)}>
                <ListItemText primary={postProcessingAction.actionDisplayName ?? postProcessingAction.actionName} />
              </ListItemButton>
            )
          )}
        </List>
      </Popover>
    </Grid>
  );
};
